import { BankingPaymentRail } from 'vopay';
import i18n from '@/i18n/i18n';

export type BankingRailDisplayType = 'standard' | 'instant' | 'same-day';

export interface BankingRailOption {
  rail: BankingPaymentRail;
  displayType: BankingRailDisplayType;
  label: string;
  description: string;
  tooltip: string;
}

/**
 * Maps BankingPaymentRail values to their display types
 * ach -> standard
 * rtp or fednow -> instant
 * fedwire -> same-day
 * eft -> standard
 * vopayinstant -> instant
 */
export const bankingRailToDisplayType = (rail: BankingPaymentRail): BankingRailDisplayType => {
  switch (rail) {
    case 'ach':
    case 'eft':
      return 'standard';
    case 'rtp':
    case 'vopayinstant':
      return 'instant';
    case 'fedwire':
      return 'same-day';
    default:
      return 'standard';
  }
};

/**
 * Gets a human-readable label for a banking rail
 */
export const getBankingRailLabel = (rail: BankingPaymentRail): string => {
  const displayType = bankingRailToDisplayType(rail);

  switch (displayType) {
    case 'standard':
      return i18n.t('BankingRail.standard', { defaultValue: 'Standard' });
    case 'instant':
      return i18n.t('BankingRail.instant', { defaultValue: 'Instant' });
    case 'same-day':
      return i18n.t('BankingRail.sameDay', { defaultValue: 'Same-day' });
    default:
      return i18n.t('BankingRail.standard', { defaultValue: 'Standard' });
  }
};

/**
 * Gets a description for a banking rail display type
 */
export const getBankingRailDescription = (rail: BankingPaymentRail): string => {
  const displayType = bankingRailToDisplayType(rail);

  switch (displayType) {
    case 'standard':
      return i18n.t('BankingRail.businessDays', { defaultValue: '1-3 Business Days' });
    case 'instant':
      return i18n.t('BankingRail.instantDelivery', { defaultValue: 'Instant Delivery' });
    case 'same-day':
      return i18n.t('BankingRail.deliveredToday', { defaultValue: 'Delivered Today' });
    default:
      return i18n.t('BankingRail.businessDays', { defaultValue: '1-3 Business Days' });
  }
};

/**
 * Gets tooltip text for a banking rail display type
 */
export const getBankingRailTooltip = (rail: BankingPaymentRail): string => {
  const displayType = bankingRailToDisplayType(rail);

  switch (displayType) {
    case 'instant':
      return i18n.t('BankingRail.instantTooltip', { defaultValue: 'Funds are delivered immediately, 24/7' });
    case 'same-day':
      return i18n.t('BankingRail.sameDayTooltip', {
        defaultValue:
          'Arrives the same business day when submitted before the daily cutoff time. Recommended for urgent or high-value transfers.',
      });
    case 'standard':
      return i18n.t('BankingRail.standardTooltip', {
        defaultValue: 'Standard processing time of 1-3 business days. Most economical option for non-urgent transfers.',
      });
    default:
      return '';
  }
};

/**
 * Converts an array of BankingPaymentRail to grouped display options
 * Groups rails by their display type to avoid duplicates
 */
export const getBankingRailOptions = (rails: BankingPaymentRail[]): BankingRailOption[] => {
  const uniqueDisplayTypes = new Map<BankingRailDisplayType, BankingPaymentRail>();

  // Group by display type, keeping the first rail of each type
  rails.forEach((rail) => {
    const displayType = bankingRailToDisplayType(rail);
    if (!uniqueDisplayTypes.has(displayType)) {
      uniqueDisplayTypes.set(displayType, rail);
    }
  });

  // Convert to array of options
  const options: BankingRailOption[] = [];

  uniqueDisplayTypes.forEach((rail, displayType) => {
    options.push({
      rail,
      displayType,
      label: getBankingRailLabel(rail),
      description: getBankingRailDescription(rail),
      tooltip: getBankingRailTooltip(rail),
    });
  });

  return options;
};
